// Constants
import { ActionTypes } from 'constants/MogamboConstants';
import { GOOGLE_ANALYTICS, PERMISSION_LEVELS, MIX_PANEL } from '@src/const';
import { showRequestFailureAlert } from 'actions/ApiAction';
import { BOT_PERSONALITIES } from 'constants/Domain';

// Utils
import { dispatch } from '@src/dispatcher';

// API
import * as Api from '@src/api';

// Store
import BotStore from '@src/store';

// Local
import {
  userHasPermission,
  permissionErrorAlert,
  sendGoogleAnalyticsEvents,
} from './_utils';

export async function createBot(botData, callback, isShowSuccess = true) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)) {
    return permissionErrorAlert();
  }

  dispatch(ActionTypes.CREATE_BOT, {
    message: `Creating a new Bot: ${botData.domain_name}`,
  });

  Api.createBot(botData)
    .then((response) => {
      if (response.success) {
        const { body, meta } = response;

        if(isShowSuccess)
        dispatch(ActionTypes.CREATE_BOT_SUCCESS, {
          data: body,
          meta,
        });

        sendGoogleAnalyticsEvents(
          GOOGLE_ANALYTICS.CATEGORY.BOT,
          GOOGLE_ANALYTICS.EVENTS.BOT_CREATE,
          `bot-${body.bot_id}`,
          GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
        );

        const mixPanelTracker = BotStore.getMixPanel();
        mixPanelTracker.track({
          event: MIX_PANEL.BOT.BOT_CREATE,
          payload: {
            'Time of Event': new Date().toString(),
            'Bot Name': botData.domain_name,
            'Bot Partner': botData.partner_name,
            'Bot Business': botData.business_name,
            'Bot Languages': botData.supported_languages,
            'Bot Languages Count': botData.supported_languages.length,
            'Bot Status': botData.active ? 'Active' : 'Inactive',
            'Bot Personality':
              BOT_PERSONALITIES[botData.bot_properties.persona || 0],
          },
        });
      } else {
        dispatch(ActionTypes.CREATE_BOT_FAILED, {
          message: response.error || 'There was an error in creating the bot',
        });
      }
      if (callback) callback(response);
    })
    .catch((error) => {
      dispatch(ActionTypes.CREATE_BOT_FAILED, {
        message: error || 'There was an error in creating the bot',
      });
      if (callback) callback({success:false});
    });
}

export function resetCreateBotModalStatus() {
  dispatch(ActionTypes.UPDATE_CREATE_BOT_MODAL_STATUS, {
    created: false,
    botId: null,
    setupData: null,
  });
}

export async function fetchTestConfiguration(botId, callback) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_VIEW)) {
    return permissionErrorAlert();
  }

  dispatch(ActionTypes.FETCH_BOT_TEST_CONFIG, {
    message: 'Fetching test configuration for your bot',
  });

  const response = await Api.fetchTestConfiguration({ bot_id: botId }).catch(
    (ex) => {
      BotStore.setTestConfigData({});
      BotStore.setTestConfigErr(ex);
      BotStore.emitChange();
    }
  );

  if (response) {
    if (response.success) {
      const { body } = response;
      BotStore.setTestConfigData(body);
      BotStore.setTestConfigErr('');
      BotStore.emitChange();

      dispatch(ActionTypes.FETCH_BOT_TEST_CONFIG_SUCCESS, {
        config: body,
      });
      callback && callback(response);
    } else {
      dispatch(ActionTypes.FETCH_BOT_TEST_CONFIG_FAILED, {
        message:
          response.error || 'Please check your Bot Testing configuration.',
      });
    }
  }
  else{
    BotStore.setTestConfigData({});
    BotStore.emitChange();
  }
}

export async function fetchTestConfigurationBusinessPartner(
  businessId,
  partner
) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_VIEW)) {
    return permissionErrorAlert();
  }

  dispatch(ActionTypes.FETCH_BOT_TEST_CONFIG, {
    message: 'Fetching business account configuration for your bot',
  });

  const response = await Api.fetchTestConfiguration({
    business_id: businessId,
    partner,
  });

  if (response.success) {
    const { body } = response;

    dispatch(ActionTypes.FETCH_BOT_TEST_CONFIG_SUCCESS, {
      config: body,
    });
  } else {
    dispatch(ActionTypes.FETCH_BOT_TEST_CONFIG_FAILED, {
      message:
        response.error || 'Please check your Business Partner configuration.',
    });
  }

  return response;
}

export function confirmDeactivateBot(bot, callback) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)) {
    return permissionErrorAlert('deactivate bot');
  }

  dispatch(ActionTypes.OPEN_CONFIRMATION_DIALOG, {
    callback,
    title: 'Deactivate Bot',
    message: 'Do you really want to deactivate this bot?',
    okTitle: 'Deactivate',
    cancelTitle: 'Cancel',
    data: {
      domain_name: bot.title,
      domain_id: bot.id,
      partner_name: bot.client,
      via_name: bot.test_business,
      active: false,
    },
  });
}

export function confirmActivateBot(bot, callback) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)) {
    return permissionErrorAlert('activate bot');
  }

  dispatch(ActionTypes.OPEN_CONFIRMATION_DIALOG, {
    callback,
    title: 'Activate Bot',
    message: 'Do you really want to activate this bot?',
    okTitle: 'Activate',
    cancelTitle: 'Cancel',
    data: {
      domain_name: bot.title,
      domain_id: bot.id,
      partner_name: bot.client,
      via_name: bot.test_business,
      active: true,
    },
  });
}

export async function getDomainData(id) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_VIEW)) {
    return permissionErrorAlert();
  }
  try {
    const response = await Api.getDomainData(id);

    if (response.success) {
      return response.body[0];
    }
    showRequestFailureAlert('Error Fetching Bot Data.');
  } catch (error) {
    showRequestFailureAlert(error || 'Error Fetching Bot Data.');
  }
}
